import React, { useState } from 'react';
import { ReactComponent as XeroLogo } from '@/assets/XeroLogo.svg';
import Button from '@/components/Button';
import RadioButtons from '@/components/RadioButtons';
import { IGetXeroTenantResponse } from '@/services/types/Xero';
import Modal from '@/components/Modal';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/utils/routes';
import useXero from '@/hooks/useXero';
import { createXeroTenantConnection } from '@/services/xero';
import { XERO_TENANT_SESSION_STORAGE_NAME } from '@/utils/browserStorageNames';
import useHomeContext from '@/hooks/useHomeContext';
import { NotificationType } from '@/context/types/Notification';

interface XeroTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenants: IGetXeroTenantResponse[];
}

const Header = () => (
  <div className="flex items-center gap-x-2">
    <XeroLogo />
    <h3 className="text-brand-xero-blue text-2xl">Access confirmed</h3>
  </div>
);

const XeroTenantModal: React.FC<XeroTenantModalProps> = ({ isOpen, onClose, tenants }) => {
  const [selectedTenant, setSelectedTenant] = useState<string>();
  const { triggerNotification } = useHomeContext()!;
  const { setXeroTenant, setIsSelectingFeed, xeroConnection, setHasExistingConnection } =
    useXero()!;
  const navigate = useNavigate();

  const handleSelectedOption = (id: string) => {
    setSelectedTenant(id);
  };

  const handleConfirm = async () => {
    const foundTenant = tenants.find((tenant) => tenant.id === selectedTenant);
    if (foundTenant) {
      if (!foundTenant?.accountId) {
        setIsSelectingFeed(true);
        await createXeroTenantConnection({
          connectionId: xeroConnection!,
          tenantId: foundTenant.id
        });

        if (sessionStorage.getItem(XERO_TENANT_SESSION_STORAGE_NAME) !== foundTenant.id) {
          setIsSelectingFeed(true);
        }
      }

      setHasExistingConnection(true);
      setXeroTenant(foundTenant);
      sessionStorage.setItem(XERO_TENANT_SESSION_STORAGE_NAME, foundTenant.id);
      triggerNotification(NotificationType.CONNECT_SUCCESS);
      onClose();
      navigate(routes.xero);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="md" headerText={<Header />}>
      <div className="p-4 flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-4">
          <p className="text-emerge-navy font-light text-sm">
            Which Xero tenant would you like to connect to your Emerge accounts?
          </p>
          <RadioButtons
            onOptionSelected={(id) => {
              handleSelectedOption(id);
            }}
            options={tenants.map((tenant) => ({ id: tenant.id, label: tenant.name }))}
          />
        </div>
        <div className="flex justify-center items-center">
          <Button variant="xero" onClick={handleConfirm}>
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default XeroTenantModal;
